// Port of SuiteModel/FindReplace.swift: project-wide find and replace over the text as stored — the Markdown the editor
// shows, emphasis markers and all — so what's found is what's seen; a span's classes (`{.u .hl-yellow}`) are syntax the
// editor hides, never text to find. Ranges are Unicode scalars (Automerge's, the Swift's) and UTF-16 (the editor's),
// so a replacement splices exactly the matched characters and comments elsewhere in the passage stay attached.
//
// Approximate: the Mac's patterns are ICU (`NSRegularExpression`), these are JavaScript's with the `u` flag. Literal
// queries, match case and whole words behave the same; in a regular expression `\w`, `\b` and `\d` are ASCII here
// (Unicode in ICU), and ICU-only syntax (possessive quantifiers, `\Q…\E`) is refused as invalid.
import { scalarOffsetsOfUTF16 } from './automerge/offsets.ts';
import { linkSyntaxRanges, spanStyle } from './proseMarkdown.ts';
import { graphemes } from './unicode.ts';

export interface FindQuery {
  text: string;
  matchCase?: boolean;
  wholeWords?: boolean;
  regularExpression?: boolean;
}

export class InvalidPattern extends Error {
  constructor(readonly pattern: string) {
    super(`“${pattern}” isn’t a valid regular expression.`);
  }
}

export interface FindMatch {
  /** Scalar offsets into the fragment's text (as the Swift's `Range<Int>`). */
  start: number;
  end: number;
  /** UTF-16 offsets (the editor's and JS Automerge's). */
  from: number;
  to: number;
  text: string;
  /** What it becomes with the replacement given (regular-expression templates expanded). */
  replacement: string;
}

/** `NSRegularExpression.escapedPattern(for:)`: every character a pattern would read as syntax, escaped. */
export const escapedPattern = (s: string) => s.replace(/[\\^$.|?*+()[\]{}/]/g, '\\$&');

/** `FindQuery.pattern()`: throws `InvalidPattern` for an invalid regular expression. */
export function findPattern(q: FindQuery): RegExp {
  let source = q.regularExpression ? q.text : escapedPattern(q.text);
  if (q.wholeWords) source = '(?<![\\p{L}\\p{N}_])(?:' + source + ')(?![\\p{L}\\p{N}_])';
  try {
    return new RegExp(source, q.matchCase ? 'gu' : 'giu');
  } catch {
    throw new InvalidPattern(q.text);
  }
}

/** A span's close, its `]` not escaped: group 1 is its classes (`spanClose`). */
const SPAN_CLOSE = /(?<!\\)\](\{[^}\n]*\})/g;

/** `ProseMarkdown.spanClassRanges(in:)`: where spans' classes are, UTF-16 [from, to). */
export function spanClassRanges(text: string): [number, number][] {
  if (!text.includes(']{')) return [];
  const out: [number, number][] = [];
  for (const m of text.matchAll(SPAN_CLOSE)) {
    const classes = m[1];
    if (spanStyle(classes) === null) continue;
    const from = m.index! + 1;
    out.push([from, from + classes.length]);
  }
  return out;
}

/** `replacementString(for:…template:)`: `$n` is group n (the longest run of digits naming a group), `\x` is x. */
export function expandTemplate(template: string, m: RegExpMatchArray): string {
  const groups = m.length - 1;
  let out = '';
  for (let i = 0; i < template.length; i++) {
    const c = template[i];
    if (c === '\\' && i + 1 < template.length) {
      out += template[++i];
    } else if (c === '$' && /[0-9]/.test(template[i + 1] ?? '')) {
      let j = i + 1;
      let n = Number(template[j]);
      j++;
      while (j < template.length && /[0-9]/.test(template[j]) && n * 10 + Number(template[j]) <= groups) n = n * 10 + Number(template[j++]);
      out += n <= groups ? (m[n] ?? '') : '';
      i = j - 1;
    } else out += c;
  }
  return out;
}

/** `FindReplace.matches(of:in:replacingWith:)`: every match in order, with what `template` makes of each (literal in a
 *  literal query). */
export function findMatches(query: FindQuery, text: string, template = ''): FindMatch[] {
  if (!query.text) return [];
  const re = findPattern(query);
  // A span's classes (`{.u .hl-yellow}`) and a link's address are syntax the editor hides, never text to find.
  const classes = [...spanClassRanges(text), ...linkSyntaxRanges(text)];
  const found: RegExpMatchArray[] = [];
  for (const m of text.matchAll(re)) {
    const from = m.index!, to = from + m[0].length;
    if (to === from) continue;
    if (classes.some(([a, b]) => Math.max(a, from) < Math.min(b, to))) continue;
    found.push(m);
  }
  const offsets = scalarOffsetsOfUTF16(text, found.flatMap((m) => [m.index!, m.index! + m[0].length]));
  return found.map((m, i) => ({
    start: offsets[2 * i],
    end: offsets[2 * i + 1],
    from: m.index!,
    to: m.index! + m[0].length,
    text: m[0],
    replacement: query.regularExpression ? expandTemplate(template, m) : template,
  }));
}

/** The label a replace's checkpoint and commit use: the query, cut to 30 characters (`Before replacing “…”`). */
export function replaceLabel(text: string): string {
  const chars = graphemes(text);
  return chars.length > 30 ? chars.slice(0, 29).join('') + '…' : text;
}
